import { type AlpineComponent } from 'alpinejs';
import BlazeSlider, {BlazeConfig} from '@builtinx/blaze-slider';
import {Breakpoints} from "./screenSizeEnum";
type SliderConfig = {
    elRef: string;
    allowKeyboardNavigation?: boolean;
    requireActiveClass?: boolean;
    slideGap?: string;
    lazyLoadImagesOnSlide?: boolean; //this requires all images to be set with a data-src attribute instead of src
    slidesToShow?: { sm: number; md: number; lg: number, xl: number } | number;
    slidesToScroll?: { sm: number; md: number; lg: number, xl: number } | number;
    alternatePaginationThreshold?: { sm: number; md: number; lg: number; xl: number };
    alternatePaginationLabel?: string | null;
}
type BlazeSliderAlpineObject = AlpineComponent<{
    init(): void;
    slideToIndex(slideIndex: number): void;
    populateLazyImages(): void;
    next(): void;
    previous(): void;
    _changeCurrentSlideIndex(pageIndex: number): void;
    _keyUpHandler(e: KeyboardEvent): void;
    _refreshSlider(): void;
    _getScreenSize(width: number): 'sm' | 'md' | 'lg' | 'xl';
    slidesToShowValue: number;
    slidesToScrollValue: number;
    sliderRef: BlazeSlider | null;
    elRef: HTMLElement | null;
    sliderConfig: BlazeConfig | null;
    currentSlideIndex: number;
    slideCount: number;
    sliderPageCount: number;
    allowKeyboardNavigation: boolean;
    showAlternatePagination: boolean;
    alternatePaginationLabel?: string | null;
}>;
function getSlidesToValue(
    config: { sm: number; md: number; lg: number; xl: number } | number | undefined,
    defaultValue: number,
    screenSize: 'sm' | 'md' | 'lg' | 'xl'
): number {
    if (typeof config === 'number') return config;
    return config ? config[screenSize] : defaultValue;
}

export function createSliderComponent(sliderConfig: SliderConfig): BlazeSliderAlpineObject {
    return {
        init() {
            const self = this;
            this.elRef = this.$refs[sliderConfig.elRef];
            this.allowKeyboardNavigation = sliderConfig.allowKeyboardNavigation ?? false;
            const screenSize = this._getScreenSize(window.innerWidth);
            if (this.elRef) {
                this.slidesToShowValue = getSlidesToValue(sliderConfig.slidesToShow, this.slidesToShowValue, screenSize);
                this.slidesToScrollValue = getSlidesToValue(sliderConfig.slidesToScroll, this.slidesToScrollValue, screenSize);
                this.sliderConfig = {
                    all: {
                        draggable: true,
                        get slidesToShow() {
                            return self.slidesToShowValue;
                        },
                        get slidesToScroll() {
                            return self.slidesToScrollValue;
                        },
                        loop: false,
                        slideGap: sliderConfig.slideGap ?? '1.5rem',
                    },
                };
                this.sliderRef = new BlazeSlider(this.elRef, this.sliderConfig);
                this.sliderRef.onSlide((slideIndex) => {
                    this._changeCurrentSlideIndex(slideIndex);
                    if(sliderConfig.lazyLoadImagesOnSlide) {
                        this.populateLazyImages();
                    }
                });
                this.slideCount = this.sliderRef.slides.length;
                this.sliderPageCount = this.sliderRef.states.length;
                if(sliderConfig.alternatePaginationThreshold) {
                    const threshold = sliderConfig.alternatePaginationThreshold[screenSize];
                    this.showAlternatePagination = threshold !== undefined && this.sliderPageCount > threshold;
                    if(sliderConfig.alternatePaginationLabel)
                        this.alternatePaginationLabel = sliderConfig.alternatePaginationLabel;
                }
                if(this.allowKeyboardNavigation){
                    document.addEventListener("keyup", e => this._keyUpHandler(e), false);
                }
                let debounceTimeout: NodeJS.Timeout | undefined;
                let previousWidth = window.innerWidth;
                window.addEventListener('resize', () => {
                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(() => {
                        const currentWidth = window.innerWidth;
                        const currentScreenSize = this._getScreenSize(currentWidth);
                        if (currentWidth !== previousWidth) {
                            if (typeof sliderConfig.slidesToShow === 'object' || typeof sliderConfig.slidesToScroll === 'object') {
                                this.slidesToShowValue = getSlidesToValue(sliderConfig.slidesToShow, this.slidesToShowValue, currentScreenSize);
                                this.slidesToScrollValue = getSlidesToValue(sliderConfig.slidesToScroll, this.slidesToScrollValue, currentScreenSize);
                                this.sliderRef?.refresh();
                                if (this.sliderRef !== null) {
                                    this.currentSlideIndex = 0;
                                    this.sliderPageCount = this.sliderRef.states.length;
                                }
                            }
                            if(sliderConfig.alternatePaginationThreshold) {
                                const currentThreshold = sliderConfig.alternatePaginationThreshold[currentScreenSize];
                                this.showAlternatePagination = currentThreshold !== undefined && this.sliderPageCount > currentThreshold;
                            }
                            previousWidth = currentWidth;
                        }
                        if(sliderConfig.lazyLoadImagesOnSlide) {
                            this.populateLazyImages();
                        }
                    }, 250);
                });
                if(sliderConfig.lazyLoadImagesOnSlide) {
                    this.populateLazyImages();
                }
            }
        },
        populateLazyImages: function () {
            const pageState = this.sliderRef?.states[this.sliderRef?.stateIndex];
            let range = pageState?.page?.map(Math.ceil) || [0, 0];
            const slides = Array.from(this.sliderRef?.slides as HTMLCollectionOf<HTMLElement>);
            const slidesArray = slides.slice(range[0], range[1] + 1);
            if(slidesArray.length > 0){
                slidesArray.forEach((slide: HTMLElement) => {
                    const images = Array.from(slide.getElementsByTagName('IMG'));
                    if (images.length > 0) {
                        images.forEach((img: Element) => {
                            const dataSrc = img.getAttribute('data-src');
                            if (dataSrc !== null) {
                                img.setAttribute('src', dataSrc);
                                img.removeAttribute('data-src');
                            }
                        });
                    }
                });
            }
        },
        slideToIndex: function (slideIndex: number) {
            if(slideIndex > this.currentSlideIndex){
                const slidesToMove = slideIndex - this.currentSlideIndex;
                this.sliderRef?.next(slidesToMove);
            }else if(slideIndex < this.currentSlideIndex){
                const slidesToMove = this.currentSlideIndex - slideIndex;
                this.sliderRef?.prev(slidesToMove);
            }
        },
        next: function () {
            this.sliderRef?.next();
        },
        previous: function () {
            this.sliderRef?.prev();
        },
        _changeCurrentSlideIndex: function (slideIndex: number) {
            this.currentSlideIndex = slideIndex;
        },
        _keyUpHandler: function (e) {
            if(sliderConfig.requireActiveClass){
                if (this.elRef === null || !this.elRef.classList.contains('active')) {
                    return;
                }
            }
            if (e.key === "ArrowRight") {
                this.next();
            }
            if (e.key === "ArrowLeft") {
                this.previous();
            }
        },
        _refreshSlider: function () {
            this.sliderRef?.refresh();
        },
        _getScreenSize: function (width: number): 'sm' | 'md' | 'lg' | 'xl' {
            if (width < Breakpoints.md) {
                return 'sm'; // Small screens
            } else if (width >= Breakpoints.md && width < Breakpoints.lg) {
                return 'md'; // Medium screens
            } else if (width >= Breakpoints.lg && width < Breakpoints.xl) {
                return 'lg'; // Large screens
            } else {
                return 'xl'; // Extra large screens
            }
        },
        slidesToShowValue: 2.5,
        slidesToScrollValue: 1,
        sliderRef: null,
        elRef: null,
        sliderConfig: null,
        currentSlideIndex: 0,
        slideCount: 0,
        sliderPageCount: 0,
        allowKeyboardNavigation: false,
        alternatePaginationLabel: 'Option',
        showAlternatePagination: false
    };
}
